import { defineStore } from 'pinia'
import Sources, { flattenFieldMappings } from '@/api/Sources'
import Enrichments from '@/api/Enrichments'
import Ip from '@/api/Ip'
import { enrichmentIcon } from '@/utils/enrichmentIcons'
import placeholderSources from '@/data/sources.json'

// Meta fields — convenience aliases that expand server-side.
// These will eventually be replaced by dynamic definitions from the API.
export const META_FIELDS = [
  { name: 'port', type: 'Meta', description: 'Source or destination port → id.orig_p, id.resp_p' },
  { name: 'ip', type: 'Meta', description: 'Source or destination IP → id.orig_h, id.resp_h' },
  { name: 'host', type: 'Meta', description: 'HTTP host, DNS query, or TLS/SSL server name → host, query, server_name' },
  { name: 'domain', type: 'Meta', description: 'Same as host → host, query, server_name' },
  { name: 'hash', type: 'Meta', description: 'File hash (any type) → md5, sha1, sha256, hash' },
  { name: 'username', type: 'Meta', description: 'Username across all protocols → user, username, client' },
  { name: 'src_ip', type: 'Meta', description: 'Alias for id.orig_h (source/originating IP)' },
  { name: 'dst_ip', type: 'Meta', description: 'Alias for id.resp_h (destination/responding IP)' },
  { name: 'src_port', type: 'Meta', description: 'Alias for id.orig_p (source port)' },
  { name: 'dst_port', type: 'Meta', description: 'Alias for id.resp_p (destination port)' },
  { name: 'dns_name', type: 'Meta', description: 'Alias for query (DNS query name)' },
  { name: 'filename', type: 'Meta', description: 'Filename from files or SMB → filename, name' },
  { name: 'email', type: 'Meta', description: 'Email address (from/to) → mailfrom, rcptto' },
  { name: 'url', type: 'Meta', description: 'HTTP URL components → host, uri, referrer' },
  { name: 'ua', type: 'Meta', description: 'Alias for user_agent (HTTP user agent)' },
  { name: 'sni', type: 'Meta', description: 'Alias for server_name (TLS/SSL Server Name Indication)' },
  { name: 'cn', type: 'Meta', description: 'Alias for subject (X.509 certificate subject)' },
  { name: 'san', type: 'Meta', description: 'X.509 Subject Alternative Name → san.dns, san.uri, san.email, san.ip' },
  { name: 'mime', type: 'Meta', description: 'Alias for mime_type (MIME type)' },
]

export const useDataSourcesStore = defineStore('dataSources', {
  state: () => ({
    // Source groups from API (each group has .sources array, respects hide_sources)
    sourceGroups: [],
    // Standalone sources from API (not in any group)
    standaloneSources: [],
    // Enrichments granted to the current user's organization, fetched
    // from GET /enrichments and mapped to the UI item shape.
    enrichments: [],
    // Source-scoped field mappings from API: { source: { logType: [fields] } }
    fieldMappingsBySource: {},
    // Annotations by source: { source: { logType: [annotations] } }
    annotationsBySource: {},
    // Enriched-IP row schema from /query/schema/ip:
    //   { entity_type, common_fields: [...], enrichments: [{slug,...,response_fields:[...]}] }
    ipEnrichmentSchema: null,
    loading: false,
    error: null,
    initialized: false,
    // Selection state — empty means "all" (no filtering)
    selectedSources: [],   // selected source/group names
    selectedLogTypes: [],  // selected log type names
    _schemaFetchTimer: null,
    // Monotonic counter so overlapping schema fetches resolve deterministically
    // (last-dispatched wins; a slow/failed earlier response can't stomp newer data).
    _schemaGeneration: 0,
  }),

  getters: {
    // Enrichments currently active for this org (granted or BYO-connected).
    activeEnrichments: (state) => state.enrichments.filter(e => e.status === 'active'),

    // Slugs usable in a query right now: active AND with the api capability on.
    // This is the single place "what to query" is derived.
    queryableEnrichmentSlugs: (state) =>
      state.enrichments
        .filter(e => e.status === 'active' && (e.enabled || []).includes('api'))
        .map(e => e.slug),

    // Queryable slugs registered for a given entity type — the 403-safe set to
    // request for that type's bucket. The backend catalog gates each enrichment
    // by entity type, so requesting a slug not registered for the type would
    // 403; this filters to the ones that apply.
    queryableSlugsForType: (state) => (type) =>
      state.enrichments
        .filter(e => e.status === 'active'
          && (e.enabled || []).includes('api')
          && (e.entityTypes || []).includes(type))
        .map(e => e.slug),

    // Flattened field mappings: { logType: [fields] } merged across all sources
    fieldMappings: (state) => flattenFieldMappings(state.fieldMappingsBySource),

    // Sources grouped for the graph view: { groups: [...], ungrouped: [...] }
    // Merges real API sources with placeholder sources for display
    groupedSources: (state) => {
      const groups = []
      const ungrouped = []
      // Real source slugs (group names + member/standalone source names) used to
      // suppress a "contact to enable" placeholder once the real source exists.
      // Keyed by slug, never by `.id` — real ids are server UUIDs.
      const seenSlugs = new Set()

      // Real sources from API groups
      for (const group of state.sourceGroups) {
        if (group.name) seenSlugs.add(group.name)
        // Hidden shared groups render as single ungrouped nodes
        const isOwned = group.owned ?? true
        if (!isOwned && group.hide_sources) {
          ungrouped.push({
            id: 'sg-' + group.name,
            name: group.name,
            label: group.display_name,
            desc: group.description || '',
            icon: 'layer-group',
            accent: '#34d399',
            active: true,
            readonly: true,
            provider: 'Shared',
            stats: group.stats || null,
          })
          continue
        }

        const hasVisibleSources = group.sources && group.sources.length > 0
        const hasHiddenSources = (group.source_count || 0) > 0
        if (!hasVisibleSources && !hasHiddenSources) continue

        if (hasVisibleSources) {
          for (const s of group.sources) if (s.name) seenSlugs.add(s.name)
        }
        groups.push({
          id: 'sg-' + group.name,
          label: group.display_name,
          accent: '#34d399',
          stats: group.stats || null,
          items: hasVisibleSources ? group.sources.map(s => ({
            id: s.id,
            name: s.name,
            label: s.display_name,
            desc: s.description || 'Your source',
            icon: s.icon || 'database',
            accent: '#34d399',
            active: s.status === 1,
            readonly: !isOwned,
            provider: isOwned ? 'Owned' : 'Shared',
            maxSizeBytes: s.max_size_bytes || null,
            sizeBytes: s.stats?.size_bytes || 0,
            records: s.stats?.records || null,
          })) : [],
        })
      }

      // Standalone sources from API (not in any group)
      for (const s of state.standaloneSources) {
        if (s.name) seenSlugs.add(s.name)
        ungrouped.push({
          id: s.id,
          name: s.name,
          label: s.display_name,
          desc: s.description || 'Your source',
          icon: s.icon || 'database',
          accent: '#34d399',
          active: s.status === 1,
          provider: 'Owned',
          stats: s.stats || null,
          maxSizeBytes: s.max_size_bytes || null,
          sizeBytes: s.stats?.size_bytes || 0,
          records: s.stats?.records || null,
        })
      }

      // Placeholder sources (grouped and ungrouped)
      const placeholderGroupMap = {}
      for (const pg of placeholderSources.groups) {
        placeholderGroupMap[pg.id] = { ...pg, accent: '#4B5563', items: [] }
      }
      for (const item of placeholderSources.items) {
        if (seenSlugs.has(item.slug)) continue
        const entry = { ...item, active: false, disabled: true, accent: '#4B5563' }
        if (item.group && placeholderGroupMap[item.group]) {
          placeholderGroupMap[item.group].items.push(entry)
        } else {
          ungrouped.push(entry)
        }
      }
      for (const pg of Object.values(placeholderGroupMap)) {
        if (pg.items.length > 0) {
          groups.push(pg)
        }
      }

      return { groups, ungrouped }
    },

    groupedEnrichments: (state) => {
      // Only active enrichments are applied to results; locked/available catalog
      // items the API now returns must not appear in the graph. Re-attach an
      // `active` flag so node styling and edge logic can key off it.
      const active = state.enrichments
        .filter(e => e.status === 'active')
        .map(e => ({ ...e, active: true }))

      // First-party `webscout_*` enrichments belong to the "Webscout Intel"
      // group; third-party/BYO integrations (e.g. abuseipdb) render as their
      // own ungrouped nodes with their provider identity.
      const firstParty = active.filter(e => e.slug.startsWith('webscout_'))
      const ungrouped = active.filter(e => !e.slug.startsWith('webscout_'))

      const groups = firstParty.length > 0
        ? [{
            id: 'first-party-intel',
            label: 'Webscout Intel',
            accent: '#7357D2',
            items: firstParty,
          }]
        : []
      return { groups, ungrouped }
    },

    // Flattened map of field name → { type, role, label } from annotations
    annotatedFields: (state) => {
      const map = new Map()
      for (const logTypeMap of Object.values(state.annotationsBySource)) {
        for (const annotations of Object.values(logTypeMap)) {
          for (const ann of annotations) {
            for (const [role, fieldName] of Object.entries(ann.fields)) {
              if (!map.has(fieldName)) {
                map.set(fieldName, { type: ann.type, role, label: ann.label })
              }
            }
          }
        }
      }
      return map
    },

    // Whether the org has any annotation of a given selector entity type
    // (ip/domain/url/…) across accessible sources — i.e. whether the observations
    // enricher will run for that type. Tab-independent (reads the schema, not the
    // per-tab sideload state), so views can gate on it before a tab's fetch runs.
    hasSelectorAnnotations: (state) => (entityType) => {
      for (const logTypeMap of Object.values(state.annotationsBySource)) {
        for (const annotations of Object.values(logTypeMap)) {
          if (annotations.some(a => a.type === entityType)) return true
        }
      }
      return false
    },

    // Whether any geo annotations exist across all sources
    hasGeoAnnotations: (state) => {
      for (const logTypeMap of Object.values(state.annotationsBySource)) {
        for (const annotations of Object.values(logTypeMap)) {
          if (annotations.some(a => a.type === 'geo')) return true
        }
      }
      return false
    },

    // Groups country_code fields by actual field name with their applicable log types
    // Returns: [{ field: "geo.orig.country_code", logTypes: ["conn", "dns"] }, ...]
    geoFieldGroups: (state) => {
      const fieldMap = {} // field_name → Set of log types
      for (const logTypeMap of Object.values(state.annotationsBySource)) {
        for (const [logType, annotations] of Object.entries(logTypeMap)) {
          for (const ann of annotations) {
            if (ann.type !== 'geo' || !ann.fields.country_code) continue
            const field = ann.fields.country_code
            if (!fieldMap[field]) fieldMap[field] = new Set()
            fieldMap[field].add(logType)
          }
        }
      }
      return Object.entries(fieldMap).map(([field, logTypes]) => ({
        field,
        logTypes: [...logTypes],
      }))
    },

    // All log types from flattened field mappings
    allLogTypes() {
      return Object.keys(this.fieldMappings).sort()
    },

    // Flat deduplicated list of all fields for autocomplete (schema + meta)
    autocompleteFields() {
      const fieldMap = new Map()

      // Add schema fields from all sources/log types
      for (const logTypeMap of Object.values(this.fieldMappingsBySource)) {
        for (const fields of Object.values(logTypeMap)) {
          for (const field of fields) {
            const name = typeof field === 'string' ? field : field.name
            if (!fieldMap.has(name)) {
              fieldMap.set(name, {
                name,
                type: typeof field === 'string' ? 'keyword' : (field.type || 'keyword'),
                description: (typeof field === 'object' && field.description) || '',
              })
            }
          }
        }
      }

      // Add meta fields (don't overwrite schema fields)
      for (const meta of META_FIELDS) {
        if (!fieldMap.has(meta.name)) {
          fieldMap.set(meta.name, { ...meta })
        }
      }

      return [...fieldMap.values()].sort((a, b) => a.name.localeCompare(b.name))
    },

    // Set of all valid field names (for tokenizer validation)
    validFieldNames() {
      return new Set(this.autocompleteFields.map(f => f.name))
    },

    // Fields grouped by log type: { logType: [{ name, type, description }] }
    fieldsByLogType() {
      const result = {}
      const flat = this.fieldMappings
      for (const [logType, fields] of Object.entries(flat)) {
        result[logType] = fields.map(field => {
          const name = typeof field === 'string' ? field : field.name
          return {
            name,
            type: typeof field === 'string' ? 'keyword' : (field.type || 'keyword'),
            description: (typeof field === 'object' && field.description) || '',
          }
        })
      }
      return result
    },

    // Active source groups (groups that have sources, including hidden ones)
    activeSourceGroups(state) {
      return state.sourceGroups.filter(g =>
        (g.source_count || 0) > 0
        || (g.sources && g.sources.length > 0)
        || (!(g.owned ?? true) && g.hide_sources)
      )
    },

    // Active standalone sources (not in any group)
    activeStandaloneSources: (state) => state.standaloneSources.filter(s => s.status === 1),

    // Names of every source/group the current user has access to.
    // Used to filter user-controlled inputs (URL params, saved searches) so
    // we don't forward names the backend will reject with
    // "unknown source or group names". Includes inactive sources — the
    // grant exists even if the source isn't currently ingesting.
    accessibleSourceNames(state) {
      const names = new Set()
      for (const g of state.sourceGroups) {
        if (g.name) names.add(g.name)
        // Member sources of a group are valid filter targets too — the backend
        // resolves an individual member name (resolve.go ResolveNames). Without
        // these, a member selection would be stripped on URL/saved-search restore.
        for (const s of (g.sources || [])) {
          if (s.name) names.add(s.name)
        }
      }
      for (const s of state.standaloneSources) {
        if (s.name) names.add(s.name)
      }
      return names
    },

    // Groups the user can drill into to pick individual member sources: owned
    // groups that expose their members. Hidden shared groups and empty groups
    // are excluded — they always act as a single unit. Map name → members[].
    //
    // Each member carries a `token` — the value used in the selection state and
    // sent to the backend. Normally that's just the source name. But group and
    // source slugs share one namespace, so a member may share its group's slug;
    // the backend resolves a bare shared slug to the GROUP, which would make the
    // colliding source unselectable on its own. For that member the token is
    // prefixed with `src:` (handled by ResolveNames) so it addresses the source
    // specifically. Non-colliding members keep bare-name tokens (backward
    // compatible with existing saved searches / URLs).
    expandableGroups(state) {
      const map = {}
      for (const g of state.sourceGroups) {
        const owned = g.owned ?? true
        if (!owned || !g.sources || g.sources.length === 0) continue
        map[g.name] = g.sources.map(s => ({
          ...s,
          token: s.name === g.name ? 'src:' + s.name : s.name,
        }))
      }
      return map
    },

    // Map of expandable group name → array of member selection tokens.
    groupMemberNames() {
      const map = {}
      for (const [name, members] of Object.entries(this.expandableGroups)) {
        map[name] = members.map(m => m.token)
      }
      return map
    },

    // Resolve a display name for ANY selection token: group, standalone, or a
    // member of a group, including `src:`/`grp:`-prefixed tokens. Returns the raw
    // name as a last resort.
    displayNameFor: (state) => (token) => {
      let name = token
      let preferSource = false
      if (token.startsWith('src:')) { name = token.slice(4); preferSource = true }
      else if (token.startsWith('grp:')) { name = token.slice(4) }

      const findSource = () => {
        const standalone = state.standaloneSources.find(s => s.name === name)
        if (standalone) return standalone.display_name
        for (const g of state.sourceGroups) {
          const member = (g.sources || []).find(s => s.name === name)
          if (member) return member.display_name
        }
        return null
      }
      const findGroup = () => {
        const group = state.sourceGroups.find(g => g.name === name)
        return group ? group.display_name : null
      }

      return (preferSource
        ? (findSource() || findGroup())
        : (findGroup() || findSource())) || name
    },

    // Map of individual source name → its own log types (derived from real field
    // mappings). Strictly per-source: do NOT merge group log types in here, or a
    // member source whose name collides with its group's name would report log
    // types that belong to sibling sources. Group-level merged log types live in
    // groupLogTypeMap.
    sourceLogTypeMap(state) {
      const result = {}
      for (const [sourceName, logTypeMap] of Object.entries(state.fieldMappingsBySource)) {
        result[sourceName] = Object.keys(logTypeMap).sort()
      }
      return result
    },

    // Map of group name → log types merged across all member sources. Used for
    // group-level views/toggles, kept separate from the per-source map above so a
    // name collision between a member source and its group doesn't cross-contaminate.
    groupLogTypeMap(state) {
      const perSource = this.sourceLogTypeMap
      const result = {}
      for (const group of state.sourceGroups) {
        const isHiddenShared = !(group.owned ?? true) && group.hide_sources
        if ((group.source_count || 0) === 0 && (!group.sources || group.sources.length === 0) && !isHiddenShared) continue
        const merged = new Set()
        // Hidden shared groups are keyed by the group name in the schema.
        if (perSource[group.name]) {
          for (const lt of perSource[group.name]) merged.add(lt)
        }
        // Owned groups expose member sources by their own names.
        if (group.sources) {
          for (const s of group.sources) {
            if (perSource[s.name]) {
              for (const lt of perSource[s.name]) merged.add(lt)
            }
          }
        }
        if (merged.size > 0) {
          result[group.name] = [...merged].sort()
        }
      }
      return result
    },

    // Convert a bare source name into the token needed to address it on the API.
    // A name that also exists as a group name must carry the `src:` prefix, or the
    // backend would resolve it to the group. Names with no group collision (and
    // names that are only groups) pass through unchanged.
    sourceToken: (state) => (name) => {
      if (!name || name.startsWith('src:') || name.startsWith('grp:')) return name
      const isGroup = state.sourceGroups.some(g => g.name === name)
      if (!isGroup) return name
      const isSource = state.standaloneSources.some(s => s.name === name)
        || state.sourceGroups.some(g => (g.sources || []).some(s => s.name === name))
      return isSource ? 'src:' + name : name
    },

    // The sources to send to the API. Empty = all (no filtering).
    effectiveSources: (state) => state.selectedSources,

    // The log types to send to the API. Empty = all (no filtering).
    effectiveLogTypes: (state) => state.selectedLogTypes,

    // Whether everything is selected (default state)
    isAllSelected: (state) => state.selectedSources.length === 0 && state.selectedLogTypes.length === 0,

    // Serialize current selection as a URL param string (empty when "all")
    serializedSources() {
      if (this.isAllSelected) return ''
      const segments = []
      // Sources
      if (this.selectedSources.length > 0) {
        segments.push('s:' + this.selectedSources.join(','))
      }
      // Log types
      if (this.selectedLogTypes.length > 0) {
        segments.push('l:' + this.selectedLogTypes.join(','))
      }
      return segments.join(';')
    },

    // Whether there are no sources configured at all
    hasSources: (state) => state.sourceGroups.some(g => (g.source_count || 0) > 0 || (g.sources && g.sources.length > 0) || (!(g.owned ?? true) && g.hide_sources)) || state.standaloneSources.length > 0,

    // Humanized label for the selector button
    selectorLabel() {
      if (!this.hasSources) return 'No Sources'
      const hasSources = this.selectedSources.length > 0
      const hasLogTypes = this.selectedLogTypes.length > 0

      if (!hasSources && !hasLogTypes) return 'All Sources'

      const parts = []
      if (hasSources) {
        if (this.selectedSources.length === 1) {
          parts.push(this.displayNameFor(this.selectedSources[0]))
        } else {
          parts.push(`${this.selectedSources.length} Sources`)
        }
      }
      if (hasLogTypes) {
        if (this.selectedLogTypes.length === 1) {
          parts.push(this.selectedLogTypes[0])
        } else {
          parts.push(`${this.selectedLogTypes.length} logs`)
        }
      }
      return parts.join(' \u00b7 ')
    },
  },

  actions: {
    async init() {
      // Only take the cheap background-refresh path when a schema is actually
      // present. A failed boot preload can leave initialized=true with empty
      // mappings; fall through to the full awaited load to recover in that case.
      if (this.initialized && Object.keys(this.fieldMappingsBySource).length > 0) {
        // Refresh schema in the background so newly ingested log types appear
        // without a full reload.
        this.fetchSchema()
        return
      }
      this.loading = true
      this.error = null
      try {
        await Promise.all([
          this.fetchSources(),
          this.fetchEnrichments(),
          this.fetchSchema(),
          this.fetchIpEnrichmentSchema(),
        ])
        this.initialized = true
      } catch (err) {
        this.error = err.message || 'Failed to load data sources'
      } finally {
        this.loading = false
      }
    },

    async fetchSources() {
      const response = await Sources.list()
      if (response.status === 'success' && response.data) {
        this.sourceGroups = response.data.groups || []
        this.standaloneSources = response.data.sources || []
      }
    },

    async fetchEnrichments() {
      const response = await Enrichments.list()
      if (response.status === 'success' && Array.isArray(response.data)) {
        this.enrichments = response.data.map(def => ({
          id: def.slug,
          slug: def.slug,
          label: def.display_name || def.slug,
          desc: def.description || '',
          icon: enrichmentIcon(def.icon),
          accent: '#7357D2',
          // BYO cards are third-party integrations; granted cards are Webscout's.
          provider: def.access_mode === 'byo' ? (def.display_name || def.slug) : 'Webscout',
          providerLogo: def.access_mode !== 'byo',
          accessMode: def.access_mode,
          status: def.status,
          // Entity types this enrichment can run against (ip, domain, …).
          entityTypes: def.entity_types || [],
          supports: def.supports || [],
          enabled: def.enabled || [],
          artifacts: def.artifacts || [],
          settings: def.settings || [],
          // Clickable → opens /selector/integration/<slug> when the enricher
          // implements the backend Statable interface.
          hasDashboard: def.has_dashboard === true,
        }))
      } else {
        this.enrichments = []
      }
    },

    // connectEnrichment stores a BYO credential (server validates it live) and
    // refreshes so the card flips to active. Returns the service result so the
    // form can surface inline errors.
    async connectEnrichment(slug, settings) {
      const result = await Enrichments.connectCredential(slug, settings)
      if (result.status === 'success') await this.fetchEnrichments()
      return result
    },

    // disconnectEnrichment removes a BYO credential and refreshes.
    async disconnectEnrichment(slug) {
      const result = await Enrichments.disconnectCredential(slug)
      if (result.status === 'success') await this.fetchEnrichments()
      return result
    },

    // downloadEnrichmentArtifact triggers a native browser download of a
    // downloadable artifact; the browser streams to disk and owns progress.
    downloadEnrichmentArtifact(slug, artifactId) {
      return Enrichments.download(slug, artifactId)
    },

    // Returns true when the store was updated with a fresh schema, false when
    // the response was stale or unsuccessful. Never overwrites populated mappings
    // with an error's empty payload — getSchema returns { status, data: {} } on
    // failure, and clobbering would collapse the event-search table to raw JSON.
    async fetchSchema() {
      const generation = ++this._schemaGeneration
      const response = await Sources.getSchema(this.effectiveSources)
      // Drop stale responses: a later fetch has already taken ownership.
      if (generation !== this._schemaGeneration) return false
      // Only a genuine success carries a real payload.
      if (response.status !== 'success') return false
      this.fieldMappingsBySource = response.data?.fields || {}
      this.annotationsBySource = response.data?.annotations || {}
      return true
    },

    async fetchIpEnrichmentSchema() {
      try {
        const response = await Ip.getSchema()
        if (response && response.status === 'success' && response.data) {
          this.ipEnrichmentSchema = response.data
        }
      } catch (_err) {
        // Schema is supplementary; failures don't block initialization.
      }
    },

    // Debounced schema refetch (300ms)
    _debouncedFetchSchema() {
      if (this._schemaFetchTimer) clearTimeout(this._schemaFetchTimer)
      this._schemaFetchTimer = setTimeout(() => {
        this.fetchSchema()
      }, 300)
    },

    // Toggle a source group on/off
    toggleSource(sourceName) {
      const idx = this.selectedSources.indexOf(sourceName)
      if (idx === -1) {
        this.selectedSources = [...this.selectedSources, sourceName]
      } else {
        const next = this.selectedSources.filter(s => s !== sourceName)
        // Don't allow deselecting the last source
        if (next.length === 0 && this.selectedLogTypes.length === 0) return
        this.selectedSources = next
      }

      this._debouncedFetchSchema()
    },

    // Toggle an expandable (owned) group as a whole. Selecting the group clears
    // any individually-selected members and the group's own log types; the group
    // name then stands in for all its members. Deselecting removes the group
    // name, its members, and its log types.
    toggleGroup(groupName) {
      const members = this.groupMemberNames[groupName] || []
      const groupLogTypes = this.groupLogTypeMap[groupName] || []

      if (this.isSourceFullySelected(groupName) && !this.isAllSelected) {
        const remove = new Set([groupName, ...members])
        const nextSources = this.selectedSources.filter(s => !remove.has(s))
        const nextLogTypes = this.selectedLogTypes.filter(lt => !groupLogTypes.includes(lt))
        // Don't allow deselecting the last source
        if (nextSources.length === 0 && nextLogTypes.length === 0) return
        this.selectedSources = nextSources
        this.selectedLogTypes = nextLogTypes
      } else {
        const memberSet = new Set(members)
        const nextSources = this.selectedSources.filter(s => !memberSet.has(s))
        if (!nextSources.includes(groupName)) nextSources.push(groupName)
        this.selectedSources = nextSources
        this.selectedLogTypes = this.selectedLogTypes.filter(lt => !groupLogTypes.includes(lt))
      }

      this._debouncedFetchSchema()
    },

    // Toggle a single member source of an owned group. If the group is currently
    // selected as a whole, expand it into its members first so the toggle removes
    // exactly one. When every member ends up selected, collapse back to the group
    // name to keep the representation canonical.
    toggleMember(groupName, memberName) {
      const members = this.groupMemberNames[groupName] || []

      if (this.isAllSelected) {
        // From "all", picking one member filters down to just that member —
        // matching the store's "only this" convention for first selections.
        this.selectedSources = [memberName]
        this.selectedLogTypes = []
        this._debouncedFetchSchema()
        return
      }

      let working = [...this.selectedSources]
      const groupIdx = working.indexOf(groupName)
      if (groupIdx !== -1) {
        working.splice(groupIdx, 1)
        for (const m of members) if (!working.includes(m)) working.push(m)
      }

      const memberIdx = working.indexOf(memberName)
      if (memberIdx !== -1) working.splice(memberIdx, 1)
      else working.push(memberName)

      // Don't allow deselecting the last source
      if (working.length === 0 && this.selectedLogTypes.length === 0) return

      // Collapse to the group name once all members are selected
      if (members.length > 0 && members.every(m => working.includes(m))) {
        working = working.filter(m => !members.includes(m))
        working.push(groupName)
      }

      this.selectedSources = working
      this._debouncedFetchSchema()
    },

    // Toggle a single log type
    toggleLogType(logType) {
      // Transition from implicit "all" to explicit: select everything except this one
      if (this.selectedLogTypes.length === 0) {
        this.selectedLogTypes = this.allLogTypes.filter(lt => lt !== logType)
  
        return
      }

      const idx = this.selectedLogTypes.indexOf(logType)
      if (idx === -1) {
        this.selectedLogTypes = [...this.selectedLogTypes, logType]
      } else {
        this.selectedLogTypes = this.selectedLogTypes.filter(lt => lt !== logType)
      }

      // Check if nothing is left
      if (this.selectedLogTypes.length === 0) {
        // Back to all log types (not "none")
        return
      }

      // If everything is now selected, reset to empty (= all)
      if (this.selectedLogTypes.length >= this.allLogTypes.length) {
        this.selectedLogTypes = []
      }

    },

    // Reset to all (no filtering)
    resetSelection() {
      this.selectedSources = []
      this.selectedLogTypes = []

      this._debouncedFetchSchema()
    },

    // Select exactly one log type, replacing the entire selection
    selectExclusiveLogType(logType) {
      this.selectedLogTypes = [logType]

    },

    // Select a single source group exclusively
    selectExclusiveSource(sourceName) {
      this.selectedSources = [sourceName]
      this.selectedLogTypes = []

      this._debouncedFetchSchema()
    },

    // Check if a source/group is fully selected. A group also counts as full
    // when every one of its members is individually selected.
    isSourceFullySelected(sourceName) {
      if (this.selectedSources.length === 0) return true // all = everything selected
      if (this.selectedSources.includes(sourceName)) return true
      const members = this.groupMemberNames[sourceName]
      if (members && members.length > 0 && members.every(m => this.selectedSources.includes(m))) {
        return true
      }
      return false
    },

    // A group is partially selected when some — but not all — of its members
    // are individually selected.
    isSourcePartiallySelected(sourceName) {
      if (this.selectedSources.length === 0) return false
      if (this.selectedSources.includes(sourceName)) return false
      const members = this.groupMemberNames[sourceName]
      if (members && members.length > 0) {
        const count = members.filter(m => this.selectedSources.includes(m)).length
        if (count > 0 && count < members.length) return true
      }
      return false
    },

    // Whether a specific member of a group is selected (directly, via the group,
    // or because everything is selected).
    isMemberSelected(groupName, memberName) {
      if (this.selectedSources.length === 0) return true
      if (this.selectedSources.includes(memberName)) return true
      if (this.selectedSources.includes(groupName)) return true
      return false
    },

    // Deserialize URL params back into selection state.
    //
    // Source names are filtered against the user's accessible set once the
    // store has loaded — pasted URLs, shared links, and stale saved searches
    // can carry names the user no longer has access to, and forwarding those
    // would trigger backend "unknown source or group names" errors. During
    // the initial bootstrap (initialized=false) we keep the parsed names as
    // a best-effort restoration; the next interaction will re-validate.
    deserializeSources(serialized) {
      if (!serialized) {
        this.resetSelection()
        return
      }

      let sources = []
      let logTypes = []
      for (const seg of serialized.split(';')) {
        if (seg.startsWith('s:')) {
          sources = seg.slice(2).split(',').filter(Boolean)
        } else if (seg.startsWith('l:')) {
          logTypes = seg.slice(2).split(',').filter(Boolean)
        }
      }

      if (this.initialized) {
        const accessible = this.accessibleSourceNames
        sources = sources.filter(n => {
          const bare = (n.startsWith('src:') || n.startsWith('grp:')) ? n.slice(4) : n
          return accessible.has(bare)
        })
      }

      this.selectedSources = sources
      this.selectedLogTypes = logTypes

      this._debouncedFetchSchema()
    },
  },
})
